import React from 'react'
import { connect } from 'react-redux'
import InlineNotification from '@cx/couturier/InlineNotification'
import styled, { css } from 'styled-components'
import { bindActionCreators } from 'redux'
import config from '../utils/config.js'
import { PwdResetLink } from './component-styles'
import { formPasswordError, setResponseErrorModal } from '../store/actions'
import useHeapTracker from '../hooks/use-heap-tracker.js'
import { LOGIN_ATTEMPT_TRACKING } from '../consts/tracking.js'

const styles = css`
  && {
    visibility: ${(props) => (props.isShown ? 'visible' : 'hidden')};
    min-height: ${(props) => (props.isShown ? 'auto' : '40px')};
    max-height: ${(props) => (props.isShown ? 'auto' : '40px')};
    margin-top: ${(props) => (props.isShown ? '24px' : '0px')};
    margin-bottom: ${(props) => (props.isShown ? '24px' : '0px')};

    padding-top: 1rem;
    padding-bottom: 1rem;

    .ui-inline-notification-icon {
      ${({ theme: { media } }) =>
        media.smallMedium(css`
          display: none;
        `)}
    }

    .ui-inline-notification-message {
      &,
      p {
        font-size: 14px;
        p:first-of-type {
          margin-bottom: 8px;
        }
      }
    }
  }
`

const Notification = styled(InlineNotification)`
  ${styles}
`

const NotificationPlaceholder = styled.div`
  ${styles}
`

const showError = (usernameError, passwordError, responseErrorModal, hasBookmarkError) => {
  if (usernameError.length > 0 || passwordError.length > 0 || responseErrorModal) {
    return { shouldShowError: true, errorType: 'error' }
  }

  if (hasBookmarkError) {
    return { shouldShowError: true, errorType: 'warning' }
  }

  return { shouldShowError: false }
}

const ErrorMessage = ({ usernameError, passwordError, responseErrorModal, hasBookmarkError, attempt }) => {
  const usernameUrl = config.client.recoverUsername
  const passwordUrl = config.client.recoverPassword
  const hasCredentialError = usernameError || passwordError

  if (responseErrorModal) {
    return (
      <>
        Sorry, we&apos;re having trouble logging you in. Please try again or contact Customer Service at <a href="tel:+8669504644">(866) 950-4644</a>.
      </>
    )
  }
  if (hasCredentialError && attempt >= 3) {
    return (
      <>
        Having trouble? If needed, recover your {' '}
        {usernameUrl ? (
          <PwdResetLink href={usernameUrl}>username</PwdResetLink>
        ) : (
          'username'
        )}{' '}
        or reset your{' '}
        {passwordUrl ? (
          <PwdResetLink href={passwordUrl}>password</PwdResetLink>
        ) : (
          'password'
        )}
        .
      </>
    )
  }
  if (usernameError) {
    return usernameError
  }
  if (passwordError) {
    return passwordError
  }
  if (hasBookmarkError) {
    return (
      <>
        <p>
          Please <a href="https://www.northwesternmutual.com/">update</a> your bookmarked link to easily log in to your account.
        </p>
        <p>Third Party Administrators please contact your advisor for online access.</p>
      </>
    )
  }

  return null
}
// Please update your bookmarked link to easily log in to your account.
// Third Party Administrators please contact your advisor for online access.

const ErrorBanner = ({
  usernameError,
  passwordError,
  responseErrorModal,
  hasBookmarkError,
  attempt,
  dispatchSetResponseErrorModal,
  dispatchSetFormPasswordError
}) => {
  const { shouldShowError, errorType } = showError(usernameError, passwordError, responseErrorModal, hasBookmarkError)
  const trackAction = useHeapTracker()
  const baselineAttempt = React.useRef(null)
  const lastTrackedAttempt = React.useRef(null)

  React.useEffect(() => {
    if (baselineAttempt.current === null) {
      baselineAttempt.current = attempt
      lastTrackedAttempt.current = attempt
    }

    const hasCredentialError = usernameError || passwordError
    const attemptCount = attempt - baselineAttempt.current

    if (attemptCount > 0 && hasCredentialError && attempt !== lastTrackedAttempt.current) {
      lastTrackedAttempt.current = attempt
      trackAction(LOGIN_ATTEMPT_TRACKING, { attemptCount })
    }
  }, [attempt, usernameError, passwordError])

  React.useEffect(() => {
    if (passwordError) {
      dispatchSetFormPasswordError()
    }
    if (window.hasResponseErrorModal) {
      dispatchSetResponseErrorModal()
    }
  }, [])

  if (shouldShowError) {
    return (
      <Notification
        aria-atomic="true"
        aria-live="assertive"
        aria-relevant="all"
        canClose={false}
        hasBookmarkError={hasBookmarkError}
        id="error-box"
        isOpen
        isShown={shouldShowError}
        type={errorType}
      >
        <ErrorMessage
          attempt={attempt}
          hasBookmarkError={hasBookmarkError}
          passwordError={passwordError}
          responseErrorModal={responseErrorModal}
          usernameError={usernameError}
        />
      </Notification>
    )
  }

  return <NotificationPlaceholder />
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      dispatchSetResponseErrorModal: setResponseErrorModal,
      dispatchSetFormPasswordError: formPasswordError
    },
    dispatch
  )

const mapStateToProps = (state) => ({
  usernameError: state.form.usernameError,
  passwordError: state.form.passwordError,
  responseErrorModal: state.submit.responseErrorModal,
  hasBookmarkError: state.form.hasBookmarkError,
  attempt: state.track.attempt
})

export default connect(mapStateToProps, mapDispatchToProps)(ErrorBanner)
